import classnames from 'classnames';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ButtonV3Icon } from '../../components/Button/ButtonV3Icon';
import type { EditorOutputData } from '../../editor/editorjs/editorjs-utils';
import { ReactComponent as ArrowNext } from '../../icons/icon-arrow-next.svg';
import { stripHtml } from '../../utils/dom';
import { parseJSONString } from '../../utils/json';
import { MessageContent } from './MessageContent/MessageContent';

const messages = defineMessages({
  readMore: { defaultMessage: 'Lire la suite', id: 'wyK7au' },
});

// declare 20% cropping margin to avoid a crop for just 1 line of text left
const CROPPING_MARGIN = 0.2;

/**
 *
 * @param children
 * @param limit
 * @param timeToDeploy
 * @constructor
 */
export const MessageLimiterDomParser = ({
  children,
  contentString = '',
  timeToDeploy = 2000,
  charsLimit = 350,
  embeds,
}: {
  contentString: string;
  children: ReactNode;
  charsLimit?: number;
  timeToDeploy?: number;
  embeds?: string;
}) => {
  const parsedEmbeds = parseJSONString<EditorOutputData>(embeds);
  const isOnlyOneLinkEmbed =
    parsedEmbeds?.blocks?.length === 1 && parsedEmbeds?.blocks[0]?.type === 'linkTool';

  const [isContentCropped, setContentCropped] = useState(false);
  const [isContentStringTooLong, setContentStringTooLong] = useState(false);
  const timerRef = useRef<number>();

  const contentLength = useMemo(() => stripHtml(contentString).length, [contentString]);

  const onMouseEnter = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (isContentStringTooLong) {
        setContentCropped(false);
      }
    }, timeToDeploy);
  }, [isContentStringTooLong, timeToDeploy]);

  const onMouseLeave = useCallback(() => {
    clearTimeout(timerRef.current);
  }, [timerRef]);

  useEffect(() => {
    if (isOnlyOneLinkEmbed) return;

    const tooLong = contentLength > charsLimit + charsLimit * CROPPING_MARGIN;

    if (tooLong) {
      setContentStringTooLong(tooLong);
      setContentCropped(tooLong);
    }
  }, [contentLength, charsLimit, isOnlyOneLinkEmbed]);

  return (
    <div
      className="limitedContent flex flex-col gap-1"
      onMouseEnter={isContentCropped ? onMouseEnter : undefined}
      onMouseLeave={isContentCropped ? onMouseLeave : undefined}
    >
      <div className={classnames('rawContent', isContentCropped && 'hidden')}>{children}</div>

      {isContentCropped && (
        <div className="rawContentCropped prose-editor">
          <MessageContent
            content={contentString}
            embeds={embeds}
            charsLimit={isContentCropped && charsLimit}
            afterContent={
              <>
                <span className="mx-2">...</span>
                <ButtonV3Icon
                  className="text-sm inline pl-4"
                  noPadding
                  onClick={() => setContentCropped(false)}
                  icon={<ArrowNext />}
                >
                  <FormattedMessage {...messages.readMore} />
                </ButtonV3Icon>
              </>
            }
          />
        </div>
      )}
    </div>
  );
};
